import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader, Card, Button, Loader } from "@/components/ui/Primitives";
import { Send, Sparkles, Trash2, MessageCircle, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat/history");
      setMessages(Array.isArray(data) ? data : (data?.messages || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    const userMsg = { role: "user", text: text.trim(), created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    const sendText = text.trim();
    setText("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", { text: sendText });
      const reply = {
        role: "assistant",
        text: data.text || data.message || data.reply || "(no reply)",
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, reply]);
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall(true);
        // remove the user's message that wasn't answered
        setMessages((prev) => prev.slice(0, -1));
        setText(sendText);
        return;
      }
      // Map raw backend errors to friendly copy the user can act on.
      // We never surface the raw "Unknown endpoint" / 5xx detail — that
      // used to leak straight into the chat and confuse subscribers.
      const status = err?.response?.status;
      const raw = err?.response?.data?.detail || "";
      let friendly;
      if (status === 401) {
        friendly = "You're signed out. Log back in and try again.";
      } else if (status === 429) {
        friendly = "You've hit today's message limit. Take a breather — resets tomorrow.";
      } else if (status === 503 || /budget/i.test(raw)) {
        friendly = "Mark is offline right now. Try again shortly.";
      } else if (status === 404) {
        friendly = "Mark is offline right now. Try again shortly.";
      } else {
        friendly = "Something went sideways on my end. Give it another go, or ping support if it keeps happening.";
      }
      const errReply = {
        role: "assistant",
        text: friendly,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errReply]);
    } finally { setSending(false); }
  };

  const clear = async () => {
    if (!confirm("Clear all chat history?")) return;
    try {
      await api.delete("/chat/clear");
      setMessages([]);
      toast.success("History cleared");
    } catch { toast.error("Could not clear"); }
  };

  const SUGGESTIONS = [
    "How much can I increase rent in Ontario?",
    "Draft a friendly late-rent reminder to my tenant.",
    "What's the standard deposit allowed in BC?",
    "Suggest a maintenance schedule for a 4-unit duplex.",
  ];

  return (
    <div>
      <PageHeader
        title="Ask Mark Anything"
        subtitle="Mark is your rental assistant — ask anything about your rentals, leases, or Canadian landlord rules."
        actions={messages.length > 0 && (
          <Button variant="secondary" onClick={clear} data-testid="clear-chat-button">
            <Trash2 className="w-4 h-4"/> Clear
          </Button>
        )}
      />

      <Card className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]">
        {paywall && (
          <div className="m-4 mb-0 p-5 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 flex items-start gap-4" data-testid="assistant-paywall">
            <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-none">
              <Lock className="w-5 h-5"/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-slate-900">Ask Mark Anything is a paid feature</div>
              <p className="text-sm text-slate-700 mt-1">Subscribe to any Central Rentals plan to unlock unlimited expert answers, lease drafting, and tenant-comms templates.</p>
              <Link to="/billing">
                <Button className="mt-3" data-testid="assistant-upgrade-button">View plans <ArrowRight className="w-4 h-4"/></Button>
              </Link>
            </div>
            <button onClick={() => setPaywall(false)} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading ? <Loader/> : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto py-10">
              <div className="w-14 h-14 rounded-full bg-green-50 text-green-800 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7"/>
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900">Hi, I&apos;m Mark — your rental assistant</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">Ask me anything — from quick policy questions to drafting tenant emails. Try one of these:</p>
              <div className="mt-5 w-full space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => setText(s)} data-testid={`assistant-suggestion-${i}`}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 transition-colors text-sm text-slate-700">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                  {m.role === "assistant" && (
                    <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider font-bold text-green-800">
                      <Sparkles className="w-3 h-3"/> Mark
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl px-4 py-3 inline-flex items-center gap-2 text-slate-500">
                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce"></div>
                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0.15s" }}></div>
                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0.3s" }}></div>
              </div>
            </div>
          )}
          <div ref={endRef}></div>
        </div>

        <form onSubmit={send} className="border-t border-slate-100 p-4 flex gap-2" data-testid="assistant-form">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask Mark anything…"
            disabled={sending}
            data-testid="assistant-input"
            className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm focus:bg-white focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none transition-all"
          />
          <Button type="submit" disabled={sending || !text.trim()} data-testid="assistant-send-button">
            <Send className="w-4 h-4"/>
          </Button>
        </form>
      </Card>
    </div>
  );
}
